import autobind from 'autobind-decorator';
import type { Rule } from 'ant-design-vue/es/form';
import { Inject, Injectable, forwardRef } from '@kaokei/use-vue-service';
import { sendVerifyCode } from '~/api/login';
import { bindPhoneNumber } from '~/api/user';
import { StorageService } from '~/services/storage.service';
import {
  HT_PLATFORM,
  TENCENT_TCAPTCHA_APP_ID,
  TENCENT_TCAPTCHA_URL,
} from '~/utils/constant';
import { encode } from '~/utils/encode';
import { loadScript } from '~/utils/loadScript';
import { showErrorMsg } from '~/utils/utility';
import { ProfileService } from './profile.service';
import { RouterService } from './router.service';
import { UserService } from './user.service';

@Injectable()
export class BindphoneService {
  public visible = false;
  public formDisabled = true;
  public loading = false;
  public countdownSecond = 0;
  public countdownTimer = 0;
  public validModel: Record<string, boolean> = {
    phoneNumber: false,
    verifyCode: false,
  };
  public phoneNumberPattern = '^1\\d{10}$';
  public model = {
    countryCode: '86',
    short: 'CN',
    phoneNumber: '',
    verifyCode: '',
  };
  public rules: Record<string, Rule[]> = {
    phoneNumber: [{ validator: this.validatePhoneNumber, trigger: 'blur' }],
    verifyCode: [
      { required: true, message: '请输入验证码', trigger: 'change' },
      { pattern: /^\d{6}$/, message: '验证码为6位数字', trigger: 'change' },
    ],
  };

  @Inject(forwardRef(() => UserService))
  public userService!: UserService;

  @Inject(forwardRef(() => ProfileService))
  public profileService!: ProfileService;

  @Inject(forwardRef(() => RouterService))
  public routerService!: RouterService;

  @Inject(forwardRef(() => StorageService))
  public storageService!: StorageService;

  @autobind
  public validatePhoneNumber(rule: any, value: any) {
    if (value) {
      const pattern = new RegExp(this.phoneNumberPattern);
      if (pattern.test(value)) {
        return Promise.resolve();
      } else {
        return Promise.reject('请输入合法手机号');
      }
    } else {
      return Promise.reject('请输入手机号');
    }
  }

  public init() {
    const value = this.storageService.getTCaptureCountdown();
    if (value > 0) {
      this.countdownSecond = value;
      this.countdownTick();
    }
  }

  public setVisible(v: boolean) {
    this.visible = v;
  }

  public updateFormStatus() {
    const vm = this.validModel;
    this.formDisabled = !(vm.phoneNumber && vm.verifyCode);
  }

  public sendVerifyCode(ticket: string, randstr: string) {
    return sendVerifyCode({
      checkType: 1,
      platform: HT_PLATFORM,
      clientName: HT_PLATFORM,
      countryCode: this.model.countryCode,
      phoneNumber: encode(this.model.phoneNumber),
      ticket,
      randstr,
    });
  }

  @autobind
  public tencentCaptchaCallback(res: any) {
    if (res.ret === 0) {
      this.sendVerifyCode(res.ticket, res.randstr)
        .then(() => {
          // 完成发送验证码
          this.startCountdown();
        })
        .catch((e) => {
          showErrorMsg(e);
        });
    }
  }

  public countdownTick() {
    if (this.countdownSecond > 0) {
      this.countdownTimer = window.setTimeout(() => {
        this.countdownSecond -= 1;
        this.countdownTick();
      }, 1000);
    } else {
      this.storageService.removeTCaptureCountdown();
    }
  }

  public startCountdown() {
    this.countdownSecond = 60;
    this.storageService.setTCaptureCountdown();
    this.countdownTick();
  }

  public submit() {
    const params = {
      countryCode: this.model.countryCode,
      short: this.model.short,
      phoneNumber: encode(this.model.phoneNumber),
      verifyCode: this.model.verifyCode,
    };
    return bindPhoneNumber(params).then((newUser) => {
      // 这里主要是更新token
      this.userService.updateUser(newUser);
      // 这里才是重新获取用户信息
      this.userService
        .fetchUserInfoFromRemote()
        .then(() => {
          const needCompleteProfile = this.userService.needCompleteProfile();
          if (needCompleteProfile) {
            this.setVisible(false);
            this.profileService.setVisible(true);
          } else {
            this.routerService.reload();
          }
        })
        .catch(() => {
          // 如果获取用户信息失败，就直接刷新页面
          this.routerService.reload();
        });
    });
  }

  @autobind
  public handleOnValidate(name: any, status: boolean) {
    this.validModel[name] = status;
    this.updateFormStatus();
  }

  @autobind
  public async handleOnFinish() {
    try {
      this.loading = true;
      await this.submit();
      this.loading = false;
    } catch (e) {
      // 登录失败
      showErrorMsg(e);
      this.loading = false;
    }
  }

  @autobind
  public handleSendVerifyCode() {
    loadScript(TENCENT_TCAPTCHA_URL).then(() => {
      try {
        const captcha = new TencentCaptcha(
          TENCENT_TCAPTCHA_APP_ID,
          this.tencentCaptchaCallback,
          {},
        );
        captcha.show();
      } catch (error) {
        showErrorMsg(error);
      }
    });
  }

  public dispose() {
    window.clearTimeout(this.countdownTimer);
  }

  @autobind
  public handleLogout() {
    this.setVisible(false);
    this.userService.reset();
  }
}
